import { CONTENT } from '../content/runtime.js';
import { insidePolygon } from '../domain/geometry.js';
import { gateLayout } from '../domain/layout.js';
import { shiftsActive } from "../domain/act2.js";
import { type EquipmentKind } from '../domain/equipment.js';
import type { PadKind } from '../domain/types.js';
import { Container, Graphics, Text, Rectangle } from "pixi.js";
import type { ReadOnlyGameModel } from "yage/game/GameModel";
import { PixiViewportSystem } from 'yage/systems/render/PixiViewport';
import { Transform } from "yage/schemas/entity/Transform";
import { AirportAgent, AirportBag, AirportZone, AirportPath, AirportTrash, AirportPassenger } from "../components/index.js";
import { CONFIG, VENDING, DIVIDER, LANDSIDE_TOP, ANGRY_PATIENCE } from "../domain/config.js";
import { activeCrises, crisisPoint } from "../services/events.js";
import { missionProgress, radioIntroduced, airportChecklist, disclosePad } from '../domain/progression.js';
import { currentMission } from "../domain/missions.js";
import { tier, airlineMode, passivePerMinute } from "../domain/airports.js";
import { retainedAirports } from "../services/retained.js";
import { specialCost, dividerCost, SPECIAL_TIMERS, unlockCost, managerCost } from "../domain/economy.js";
import { state, career, stations, ids } from "../services/world.js";
import { LOCAL, send, localPlayer, localPlayers, followObjective } from "../services/local.js";
import { objectiveAction, guidanceTarget, deskHoldGuidance } from "../services/guidance.js";
import { planes } from "../services/operations.js";
import { PHASE } from "../domain/operations.js";
import { AirportModal } from "./modal.js";
import { PauseOverlay } from './pause.js';
import { AirportClock } from './clock.js';
import { airportTime, departureBoardStatus, boardingTimingText, departureTimingText } from '../domain/timetable.js';
import { DepartureMoment } from './departure.js';
import { C, rounded, label, button, bills, shape, fmt, setText } from "./primitives.js";
import { cameraDirection, rotateCamera } from './camera.js';
const MAP_TOP = 345;
/** Frames of standing on a build pad before its plan opens. */
export const BUILD_PAD_DWELL = 60;
export class AirportHUD {
    private lastNotice = '';
    readonly root = new Container();
    readonly modal: AirportModal;
    private readonly pause: PauseOverlay;
    private readonly departure: DepartureMoment;
    private panel = new Container();
    private w = 0;
    private h = 0;
    private small = false;
    get isSmall() { return this.small; }
    private money!: Text;
    private walletIcon!: Container;
    private shownMoney: number | null = null;
    private lastMoney = -1;
    private pop = 0;
    private sub!: Text;
    private missionTitle!: Text;
    private radioButton!: Container;
    private captainButton!: Container;
    private objectiveActionLabel!: Text;
    private missionBody!: Text;
    private objective!: Graphics;
    private contextTitle!: Text;
    private contextBody!: Text;
    private contextAction!: Container;
    private contextActionLabel!: Text;
    private progress!: Graphics;
    private news!: Text;
    private mini!: Graphics;
    private viewLabel!: Text;
    private minimap!: Container;
    private mapButton!: Container;
    private mapBadge!: Graphics;
    private mapBadgeCount!: Text;
    private mapOpen = false;
    private mw = 0;
    private mh = 0;
    private openedZone = -1;
    private clock!: AirportClock;
    private context = new Container();
    private newsPill!: Graphics;
    private missionX = 0;
    private missionFit = '';
    private missionY = 0;
    private missionW = 0;
    private missionH = 0;
    private contextX = 0;
    private contextY = 0;
    private contextW = 0;
    private contextH = 0;
    constructor(parent: Container) { this.root.zIndex = 900000; this.root.sortableChildren = true; parent.addChild(this.root); this.modal = new AirportModal(this.root); this.pause = new PauseOverlay(this.root); this.departure = new DepartureMoment(this.root); window.addEventListener('resize', this.resizeWindow); }
    destroy() { window.removeEventListener('resize', this.resizeWindow); this.departure.destroy(); this.pause.destroy(); this.modal.destroy(); this.root.destroy({ children: true }); }
    private resizeWindow = () => {
        const g = LOCAL.model, app = g?.getSystem(PixiViewportSystem)?.pixiApp;
        if (!g || !app) return;
        // Paused worlds do not draw another frame, but window layout must still update.
        this.resize(app.renderer.width, app.renderer.height);
        this.update(g);
        app.renderer.render(app.stage);
    };
    resize(w: number, h: number) {
        if (w === this.w && h === this.h)
            return;
        this.w = w;
        this.h = h;
        this.small = w < 700;
        this.panel.destroy({ children: true });
        this.panel = new Container();
        this.missionFit = '';
        this.root.addChildAt(this.panel, 0);
        const p = this.panel, mobile = this.small, pad = mobile ? 10 : 16;
        // One thin bar on top: wallet left; clock, map and Menu right. The objective is a slim pill beside the wallet
        // (desktop) or under the bar (phone); tapping it walks you to the next step.
        const barH = 40, menuW = 64;
        const cardW = mobile ? (w < 360 ? 88 : 110) : 150, cardH = barH;
        rounded(p, pad, pad, cardW, cardH, barH / 2, C.navy, 0.96);
        this.walletIcon = bills(p, pad + 18, pad + cardH / 2, 8);
        this.money = label(p, '40', pad + 34, pad + 3, 17, C.white, '800');
        this.sub = label(p, 'TERMINAL 1', pad + 35, pad + 23, 8, 0xb5ddd5, '700');
        button(p, 'Menu', w - pad - menuW, pad, menuW, barH, () => this.modal.open('settings'), C.navy, 13);
        const mapX = w - pad - menuW - 8 - 44;
        const clockW = 84;
        this.clock = new AirportClock(p, clockW, barH);
        this.clock.root.position.set(mapX - 8 - clockW, pad);
        this.missionX = mobile ? pad : pad + cardW + 10;
        this.missionY = mobile ? pad + barH + 8 : pad;
        this.missionW = mobile ? w - pad * 2 : Math.max(240, Math.min(420, this.clock.root.x - this.missionX - 16));
        this.missionH = mobile ? 34 : barH;
        rounded(p, this.missionX, this.missionY, this.missionW, this.missionH, this.missionH / 2, C.cream, 0.94);
        this.missionTitle = label(p, 'Your first shift', this.missionX + 14, this.missionY + this.missionH / 2 - 8, 12, C.navy, '800');
        this.missionBody = label(p, 'Tap a blue circle to work.', this.missionX + this.missionW - 52, this.missionY + this.missionH / 2 - 7, 11, C.muted, '700');
        this.missionBody.anchor.set(1, 0);
        const objectiveButton = button(p, 'GO', this.missionX + this.missionW - 44, this.missionY + (this.missionH - 26) / 2, 38, 26, () => this.guide(), C.teal, 11);
        this.objectiveActionLabel = objectiveButton.children.find(c => c instanceof Text) as Text;
        const objectiveHit = new Container();
        objectiveHit.eventMode = 'static';
        objectiveHit.cursor = 'pointer';
        objectiveHit.hitArea = new Rectangle(this.missionX, this.missionY, this.missionW - 48, this.missionH);
        objectiveHit.on('pointerdown', event => { event.stopPropagation(); if (!LOCAL.modalOpen) this.guide(); });
        p.addChild(objectiveHit);
        this.objective = shape(p);
        // Bottom: a next useful action, or an explanation of the current work/purchase.
        const by = h - pad - 44, bw = mobile ? (w - pad * 2 - 10) / 2 : 140;
        this.radioButton = button(p, 'Math Radio', mobile ? pad : w - pad - bw * 2 - 10, by, bw, 44, () => this.modal.open('puzzle'), C.teal, 14);
        this.captainButton = button(p, 'Captain', mobile ? pad + bw + 10 : w - pad - bw, by, bw, 44, () => { const g = LOCAL.model; if (g && localPlayers(g).length > 1) {
            LOCAL.slot = (LOCAL.slot + 1) % localPlayers(g).length;
            this.modal.close();
        }
        else
            this.modal.open('journal'); }, C.navy, 14);
        this.contextW = mobile ? w - pad * 2 : Math.min(460, w - pad * 2 - bw * 2 - 30);
        this.contextH = mobile ? 74 : 72;
        this.contextX = pad;
        this.contextY = mobile ? by - this.contextH - 8 : h - pad - this.contextH;
        this.context = new Container();
        p.addChild(this.context);
        rounded(this.context, this.contextX, this.contextY, this.contextW, this.contextH, 14, C.navy, 0.94);
        this.contextTitle = label(this.context, 'You are the captain', this.contextX + 14, this.contextY + 9, 14, C.white, '800', this.contextW - 92);
        this.contextBody = label(this.context, 'Tap anywhere to walk. Work begins when you arrive.', this.contextX + 14, this.contextY + 30, 11, 0xbadad5, '500', this.contextW - 92);
        this.contextAction = button(this.context, 'GO', this.contextX + this.contextW - 66, this.contextY + 26, 54, 44, () => this.assist(), C.teal, 12);
        this.contextActionLabel = this.contextAction.children.find(c => c instanceof Text) as Text;
        this.progress = shape(this.context);
        // Notices appear as a pill under the top cards instead of floating over world labels.
        this.newsPill = shape(p);
        this.news = label(p, '', w / 2, 0, mobile ? 12 : 13, C.white, '800', mobile ? w - 60 : 520);
        this.news.anchor.set(0.5, 0);
        this.news.y = this.missionY + this.missionH + 10;
        // The map icon opens the terminal map; it is badged when something on the floor needs you.
        this.mapButton = new Container();
        this.mapButton.name = 'button:Map';
        this.mapButton.position.set(mapX, pad);
        p.addChild(this.mapButton);
        rounded(this.mapButton, 0, 0, 44, 40, 20, C.navy, 0.96);
        // A folded paper map.
        shape(this.mapButton).beginFill(0xd6e7da).drawPolygon([10, 12, 18, 9, 26, 12, 34, 9, 34, 28, 26, 31, 18, 28, 10, 31]).endFill()
            .lineStyle(1.5, C.navy, 0.5).moveTo(18, 9).lineTo(18, 28).moveTo(26, 12).lineTo(26, 31);
        this.mapBadge = shape(this.mapButton);
        this.mapBadgeCount = label(this.mapButton, '', 40, -5, 10, C.white, '800');
        this.mapBadgeCount.anchor.set(0.5, 0);
        this.mapButton.eventMode = 'static';
        this.mapButton.cursor = 'pointer';
        this.mapButton.hitArea = new Rectangle(0, 0, 44, 40);
        this.mapButton.on('pointerdown', event => { event.stopPropagation(); if (!LOCAL.modalOpen) this.mapOpen = !this.mapOpen; });
        // The opened map keeps the terminal's real proportions (interior floor only); tap it to walk there.
        this.minimap = new Container();
        this.mw = mobile ? w - pad * 2 : 360;
        this.mh = Math.round((this.mw - 12) * (CONFIG.height - MAP_TOP) / CONFIG.width) + 8;
        this.minimap.position.set(w - this.mw - pad, this.missionY + this.missionH + 8);
        p.addChild(this.minimap);
        rounded(this.minimap, 0, 0, this.mw, this.mh + 74, 12, C.navy, 0.96);
        label(this.minimap, 'MAP · TAP TO WALK', 10, 6, mobile ? 10 : 11, 0xabcfc8, '800');
        const close = button(this.minimap, '×', this.mw - 34, 4, 28, 22, () => { this.mapOpen = false; }, C.teal, 16);
        close.name = 'button:Close map';
        this.mini = shape(this.minimap);
        this.mini.position.set(6, 30);
        this.mini.eventMode = 'static';
        this.mini.hitArea = new Rectangle(0, 0, this.mw - 12, this.mh - 8);
        this.mini.cursor = 'pointer';
        this.mini.on('pointerdown', event => { event.stopPropagation(); if (LOCAL.modalOpen)
            return; const pos = this.mini.toLocal(event.global); this.mapOpen = false; send('move', { x: pos.x / (this.mw - 12) * CONFIG.width, y: pos.y / (this.mh - 8) * (CONFIG.height - MAP_TOP) + MAP_TOP }); });
        this.minimap.eventMode = 'static';
        this.minimap.hitArea = new Rectangle(0, 0, this.mw, this.mh + 74);
        this.minimap.on('pointerdown', event => event.stopPropagation());
        const legendY = this.mh + 28;
        for (const [i, [color, text]] of ([[0xe5484d, 'Problem'], [0xf2994a, 'Impatient'], [0xc9a227, 'Mess']] as const).entries()) {
            shape(this.minimap).beginFill(color).drawCircle(16 + i * 66, legendY + 7, 4).endFill();
            label(this.minimap, text, 24 + i * 66, legendY, 10, 0xd6e7da, '700');
        }
        // Turning the camera lives with the map.
        button(this.minimap, '↶', this.mw - 106, legendY - 4, 40, 30, () => rotateCamera(-1), C.teal, 20);
        this.viewLabel = label(this.minimap, 'SOUTH', this.mw - 53, legendY + 30, 9, 0xabcfc8, '800');
        this.viewLabel.anchor.set(0.5, 0);
        button(this.minimap, '↷', this.mw - 52, legendY - 4, 40, 30, () => rotateCamera(1), C.teal, 20);
        this.minimap.visible = this.mapOpen;
        this.modal.resize(w, h);
        this.pause.resize(w, h);
        this.departure.resize(w, h);
    }
    /** World HTML displays sit behind these native canvas HUD panels. */
    worldOcclusion() {
        const pad = this.small ? 10 : 16, cardW = this.small ? this.w < 360 ? 88 : 110 : 150;
        const regions = [
            new Rectangle(pad, pad, cardW, 40),
            new Rectangle(this.missionX, this.missionY, this.missionW, this.missionH),
            new Rectangle(this.w - pad - 64, pad, 64, 40),
        ];
        regions.push(this.clock.root.getBounds());
        regions.push(this.mapButton.getBounds());
        if (this.minimap.visible) regions.push(new Rectangle(this.minimap.x, this.minimap.y, this.mw, this.mh + 74));
        if (this.context.visible) regions.push(new Rectangle(this.contextX, this.contextY, this.contextW, this.contextH));
        if (this.radioButton.visible) regions.push(this.radioButton.getBounds());
        if (this.captainButton.visible) regions.push(this.captainButton.getBounds());
        if (this.news.text) regions.push(this.newsPill.getBounds());
        if (this.departure.root.visible) regions.push(this.departure.root.getBounds());
        if (this.pause.root.visible) regions.push(new Rectangle(0, 0, this.w, this.h));
        return regions;
    }
    assist() {
        const g = LOCAL.model, pid = g ? localPlayer(g) : -1;
        if (!g || pid < 0) return;
        followObjective();
    }
    guide() { followObjective(); }
    update(g: ReadOnlyGameModel) {
        const e = state(g), ss = stations(g), pid = localPlayer(g);
        this.departure.update(g);
        this.clock.update(g.frame);
        setText(this.viewLabel, cameraDirection().toUpperCase());
        // The wallet counts up to its real value and pops when it grows (doc 10 §4.3).
        const money = career(g).money;
        if (this.shownMoney === null || LOCAL.reducedMotion || Math.abs(money - this.shownMoney) > Math.max(1000, this.shownMoney))
            this.shownMoney = money;
        else if (this.shownMoney !== money) {
            this.shownMoney += (money - this.shownMoney) * 0.25;
            if (Math.abs(money - this.shownMoney) < 1) this.shownMoney = money;
        }
        setText(this.money, fmt(this.shownMoney));
        if (this.lastMoney >= 0 && money > this.lastMoney && !LOCAL.reducedMotion) this.pop = 10;
        this.lastMoney = money;
        const popScale = this.pop > 0 ? 1 + 0.12 * (this.pop / 10) : 1;
        if (this.pop > 0) this.pop--;
        this.money.scale.set(popScale);
        this.walletIcon.scale.set(popScale);
        const port = tier(e), passive = passivePerMinute(e, career(g).prestige, retainedAirports(g).filter(r => r.managed).map(r => r.airportId));
        setText(this.sub, `Happiness ${e.passengerHappiness}%${passive ? ` · +${fmt(passive)}/MIN` : airlineMode(e) ? ` · ${e.pilotCount} pilots` : ''}`);
        this.modal.refresh(g.frame, e.revision);
        this.minimap.visible = this.mapOpen && !LOCAL.modalOpen;
        const m = currentMission(e), progress = missionProgress(e, ss);
        const action = pid >= 0 ? objectiveAction(g, pid) : null;
        const fullTitle = action?.view === 'prestige' ? 'Airport ready to grow' : action?.title ?? m?.title ?? 'Complete this airport';
        setText(this.missionBody, action?.view === 'prestige' ? `${airportChecklist(e,ss).length}/${airportChecklist(e,ss).length} airport goals · Review the move` : progress.text);
        setText(this.objectiveActionLabel, action?.view ? 'PLAN' : action && pid >= 0 && guidanceTarget(g, pid, action) ? 'GO' : 'WAIT');
        this.radioButton.visible = !LOCAL.focusedProgression || radioIntroduced(e);
        this.captainButton.visible = localPlayers(g).length > 1;
        // One line: the progress text keeps its place at the right, the title shortens to fit beside it.
        const fit = fullTitle + '|' + this.missionBody.text;
        if (fit !== this.missionFit) {
            this.missionFit = fit;
            this.missionTitle.text = fullTitle;
            const room = this.missionW - 70 - this.missionBody.width;
            for (let t = fullTitle; t.length > 4 && this.missionTitle.width > room;) { t = t.slice(0, -1); this.missionTitle.text = t.trimEnd() + '…'; }
        }
        const fraction = action?.view === 'prestige' ? 1 : Math.min(1, progress.value / Math.max(1,progress.target));
        const barX = this.missionX + 14, barY = this.missionY + this.missionH - 5, barW = this.missionW - 66;
        this.objective.clear().beginFill(C.line).drawRoundedRect(barX, barY, barW, 2, 1).endFill().beginFill(C.teal).drawRoundedRect(barX, barY, barW * fraction, 2, 1).endFill();
        // The banner carries only top-priority notices; the rest stay in the inbox, the map and the objective card.
        setText(this.news, e.noticeUntil > g.frame && e.noticePriority >= 3 ? e.notice : '');
        // TX-023: important Act II notices also land in the inbox (kept on this device).
        if (e.noticeUntil > g.frame && e.notice !== this.lastNotice) {
            this.lastNotice = e.notice;
            if (shiftsActive(e) && e.noticePriority >= 1) { LOCAL.inbox = [...LOCAL.inbox, e.notice].slice(-30); LOCAL.inboxUnread++; }
        }
        this.newsPill.clear();
        if (this.news.text) this.newsPill.beginFill(C.navy, 0.9).drawRoundedRect(this.news.x - this.news.width / 2 - 14, this.news.y - 6, this.news.width + 28, this.news.height + 12, 12).endFill();
        let show = false, canGuide = false; // The top GO is the only objective-navigation button.
        let title = action?.title ?? 'Tap to move', body = action?.detail ?? 'Walk onto a blue work circle. Collect bills by walking over them.', fractionPad = 0;
        setText(this.contextActionLabel, 'GO');
        if (pid >= 0) {
            const a = g.getTypedUnsafe(AirportAgent, pid), path = g.getTypedUnsafe(AirportPath, pid), bags = ids(g, AirportBag).filter(b => g.getTypedUnsafe(AirportBag, b).carrier === pid).length;
            if (a.pad !== this.openedZone) this.openedZone = -1;
            const trash = ids(g, AirportTrash).filter(t => g.getTypedUnsafe(AirportTrash, t).carrier === pid).length;
            const activePad = a.pad >= 0 ? g.getTyped(AirportZone, a.pad) : null;
            const usefulPad = activePad && (activePad.kind !== 'work' || ss[activePad.stage].busy || action?.zone === a.pad) ? activePad : null;
            if (usefulPad && (!LOCAL.focusedProgression || a.intent === a.pad || disclosePad(e, ss, usefulPad.kind as PadKind, usefulPad.stage))) {
                show = true; canGuide = false;
                const z = usefulPad, s = ss[z.stage];
                title = z.stage >= 0 ? CONFIG.stages[z.stage].name : 'Captain’s station';
                if (!LOCAL.modalOpen && !path.moving && !a.inputLocked && a.intent === a.pad && this.openedZone !== a.pad && z.kind === 'admin') {
                    this.openedZone = a.pad;
                    if (m?.metric === 'crew' || m?.metric === 'managers') this.modal.open('crew');
                    else if ((m?.metric === 'upgrades' || m?.metric === 'capacity') && m.station !== undefined) this.modal.openFacility(m.station);
                    else if (m?.pad === 'chairs') this.modal.openEquipment('chairs');
                    else this.modal.open('admin');
                }
                if (z.kind === 'admin') { title = 'Airport admin'; body = 'Helpers and airport improvements have separate screens here.'; }
                // A build pad opens its plan after a second of standing on it, however you got there.
                if (!LOCAL.modalOpen && !path.moving && !a.inputLocked && (a.intent === a.pad || z.kind === 'build' && a.dwell >= BUILD_PAD_DWELL) && this.openedZone !== a.pad && (['build', 'hire', 'vending'].includes(z.kind) || z.kind === 'upgrade' && !disclosePad(e, ss, 'upgrade', z.stage))) {
                    this.openedZone = a.pad; this.modal.openFacility(z.stage, z.kind === 'vending');
                }
                if (!LOCAL.modalOpen && !path.moving && !a.inputLocked && a.intent === a.pad && this.openedZone !== a.pad && ['pilots', 'routes', 'puzzle'].includes(z.kind)) { this.openedZone = a.pad; this.modal.open(z.kind); }
                if (!LOCAL.modalOpen && !path.moving && !a.inputLocked && a.intent === a.pad && this.openedZone !== a.pad && (z.kind === 'divider' || z.kind in SPECIAL_TIMERS)) {
                    this.openedZone = a.pad; this.modal.openEquipment(z.kind as EquipmentKind, z.stage);
                }
                if (z.kind === 'pilots') { title = 'Pilot office'; body = `${e.pilotCount} hired pilots. Hire or assign pilots here.`; }
                if (z.kind === 'routes') { title = 'Flight Planning'; body = e.pilotCount < 2 ? 'Hire two pilots to plan routes. International flying also needs Passport and Dispatch open.' : 'License destinations, choose a gate and set departure spacing.'; }
                if (z.kind === 'vending') { title = VENDING[z.stage].name; body = 'Passengers buy snacks here. Choose products or dispenser speed; collect sales income beside the machine.'; }
                if (z.kind === 'trash') { title = 'Trash disposal'; body = trash ? `Disposing of ${trash} litter…` : 'Walk over litter to pick it up. Bring the stack back here.'; }
                if (z.kind === 'work') {
                    title = `${s.busy ? 'Serving' : 'Waiting'} at ${CONFIG.stages[z.stage].name}`;
                    body = s.busy ? `Stay here while each passenger finishes.${[0, 1, 4, 5].includes(z.stage) ? ' Collect the gold cash pile when you leave.' : ' This service keeps passengers moving.'}` : 'You are in the right place. Passengers are walking to the desk.';
                    if (z.stage === 0 && m?.metric === 'manualCheckIns') body = `${progress.value}/6 checked in. ${body}`;
                    const held = z.stage !== 6 ? deskHoldGuidance(g, z.stage, pid) : null;
                    if (held) { title = held.title; body = held.detail; }
                    if (z.stage === 6) {
                        const plane = planes(g).find(p => p.gate === z.gate && p.phase === PHASE.BOARDING);
                        const question = ids(g,AirportPassenger).some(id =>  { const p = g.getTypedUnsafe(AirportPassenger,id); return p.state === 7 && p.gate === z.gate; });
                        if (question) { title = 'Answering a passenger'; body = 'Stay here to answer their question. This does not call boarding.'; }
                        else if (plane && !plane.boardingCalled) { title = plane.boardingAnnouncedFrame >= 0 ? 'Boarding shortly' : g.frame >= plane.boardingOpensFrame ? 'Ready for boarding' : `Standard boarding ${airportTime(plane.boardingOpensFrame)}`; body = `${departureTimingText(plane,g.frame)}. ${boardingTimingText(plane,g.frame)}`; }
                        else if (plane && plane.boarded >= plane.seats) { title = departureBoardStatus(plane,g.frame) === 'EARLY DEPARTURE' ? 'Early departure' : 'Everyone is aboard'; body = `All ${plane.seats} passengers are aboard. Taking off in ${Math.max(0,Math.ceil((plane.departureReadyFrame-g.frame)/60))}s. You can leave the desk.`; }
                        else body = plane ? `${plane.boarded}/${plane.seats} aboard · ${departureTimingText(plane,g.frame)}. Stay here to check tickets.` : 'The aircraft is away. Its next passengers can board after it returns.';
                    }
                }
                if (z.kind === 'hire') {
                    title = 'Hire a helper';
                    body = career(g).money < managerCost(z.stage,e,e.hires) ? `Need ${fmt(managerCost(z.stage,e,e.hires)-career(g).money)} more bills. Collect ticket or retail sales first.` : e.hires === 0 ? `Your first clerk costs ${fmt(managerCost(z.stage,e,e.hires))} bills and holds one desk. Clerks never collect cash.` : `A helper costs ${fmt(managerCost(z.stage,e,e.hires))} bills and can cover different jobs. Helpers never collect cash.`;
                }
                if (z.kind === 'upgrade') {
                    title = `${CONFIG.stages[z.stage].name} · Level ${s.level}`;
                    body = s.upgradeUntil > g.frame ? `Next improvement in ${Math.ceil((s.upgradeUntil - g.frame) / 60)}s · same timer for bills and puzzles.` : 'At the admin desk, choose bills or a three-calculation challenge. Both add one level.';
                }
                if (z.kind === 'build') {
                    title = 'Build ' + CONFIG.stages[z.stage].name;
                    body = a.building === z.stage ? `${fmt(s.invested)} / ${fmt(unlockCost(z.stage, e))} bills · walk off to pause` : 'Choose Build to approve construction. Nothing is spent until you confirm.';
                    fractionPad = s.invested / Math.max(1, unlockCost(z.stage, e));
                }
                if (z.kind === 'belt') {
                    title = 'Aircraft loading belt';
                    body = bags ? `Unloading ${bags} bags…` : 'Carry bags from the rack here to get them ready for boarding.';
                }
                if (z.kind === 'puzzle') {
                    title = 'Math Radio';
                    body = 'Stand here and the radio turns on by itself. Optional puzzles, helpful rewards.';
                }
                if (z.kind === 'prestige') {
                    title = 'Control tower';
                    body = 'Open Airport Plan to review the requirements and what carries over before moving.';
                }
                if (z.kind === 'divider' || z.kind in SPECIAL_TIMERS) {
                    const divider = z.kind === 'divider';
                    title = divider ? 'Queue dividers · Level ' + s.dividerLevel : z.kind === 'chairs' ? 'Waiting chairs · Level ' + e.chairLevel : z.kind === 'cleaning' ? 'Bigger trash stack' : z.kind === 'shuttle' ? 'Shuttle frequency' : z.kind === 'boots' ? 'Faster shoes' : 'Baggage capacity';
                    const cost = divider ? dividerCost(s, e) : specialCost(z.kind, e);
                    const until = divider ? s.dividerUntil : e[SPECIAL_TIMERS[z.kind as keyof typeof SPECIAL_TIMERS]];
                    body = !Number.isFinite(cost) ? 'Fully upgraded.' : until > g.frame ? `Ready again in ${Math.ceil((until - g.frame) / 60)}s.` : `Open the price card to buy for ${fmt(cost)} bills. Walking here never spends.`;
                    fractionPad = 0;
                }
            }
            else if (trash) {
                show = true;
                title = `Trash ${trash} / ${4 + e.trashLevel * 2}${bags ? ` · ${bags} bags` : ''}`;
                body = 'Carry litter to a green bin. Tap the bin to walk there.';
            }
            else if (bags) {
                show = true;
                title = `Carrying ${bags} / ${4 + e.bagLevel * 2} bags`;
                body = 'Walk to the loading belt in the middle of the concourse.';
            }
            // Nothing pops up while you walk; the objective pill already says where you are going.
            if (path.moving && !usefulPad) show = false;
            if (a.feedbackUntil > g.frame && !usefulPad && !path.moving) {
                show = true;
                body = a.feedback;
                if (a.feedback.startsWith('Collected ')) title = 'Cash collected';
            }
        }
        this.contextAction.visible = canGuide;
        this.contextAction.name = 'button:' + this.contextActionLabel.text;
        const textWidth = this.contextW - (canGuide ? 92 : 28);
        this.contextTitle.style.wordWrapWidth = textWidth;
        this.contextBody.style.wordWrapWidth = textWidth;
        setText(this.contextTitle, title);
        setText(this.contextBody, body);
        this.context.visible = show && !LOCAL.modalOpen;
        this.progress.clear();
        if (fractionPad > 0)
            this.progress.beginFill(C.gold).drawRoundedRect(this.contextX + 14, this.contextY + this.contextH - 8, (this.contextW - 28) * Math.min(1, fractionPad), 3, 1).endFill();
        const issues = this.issues(g);
        const count = issues.problems.length + issues.impatient.length + issues.messes.length;
        this.mapBadge.clear();
        setText(this.mapBadgeCount, count ? String(Math.min(count, 99)) : '');
        if (count) this.mapBadge.beginFill(issues.problems.length ? 0xe5484d : issues.impatient.length ? 0xf2994a : 0xc9a227).drawCircle(40, 2, 9).endFill();
        if (!this.minimap.visible) return;
        const mx = (this.mw - 12) / CONFIG.width, my = (this.mh - 8) / (CONFIG.height - MAP_TOP);
        this.mini.clear().beginFill(0xd6e7da, 0.14).drawRoundedRect(0, 0, this.mw - 12, this.mh - 8, 4).endFill();
        // Airside above the security wall, landside below it; land past the fence is shaded.
        const zones = (CONTENT.layout as { ZONES?: { zone: string; ring: number[] }[] }).ZONES;
        if (zones) for (const z of zones.filter(z => z.zone === 'airside')) this.mini.beginFill(0x8fa6e6, 0.22).drawPolygon(z.ring.map((v, i) => i % 2 ? (v - MAP_TOP) * my : v * mx)).endFill();
        else this.mini.beginFill(0x8fa6e6, 0.22).drawRect(0, 0, this.mw - 12, (LANDSIDE_TOP - MAP_TOP) * my).endFill();
        for (const r of gateLayout(tier(state(g)).code).DIVIDER) this.mini.beginFill(0xd6e7da, 0.8).drawRect((r.x - r.w / 2) * mx, (r.y - r.h / 2 - MAP_TOP) * my, r.w * mx, Math.max(2, r.h * my)).endFill();
        const maxX = tier(state(g)).maxX;
        if (maxX < CONFIG.width) this.mini.beginFill(0x10213f, 0.55).drawRect(maxX * mx, 0, (CONFIG.width - maxX) * mx, this.mh - 8).endFill();
        // An airport with its own outline: everything outside it is shaded.
        const outline = gateLayout(tier(state(g)).code).OUTLINE;
        if (outline) for (const r of outsideRuns(outline)) this.mini.beginFill(0x10213f, 0.75).drawRect(r.x * mx, (r.y - MAP_TOP) * my, r.w * mx, r.h * my).endFill();
        ss.forEach((s, i) => { const p = CONFIG.stages[i]; if (outline && p.y > 345 && !insidePolygon(outline, p.x, p.y)) return; this.mini.beginFill(s.unlocked ? p.color : 0x78938c, s.unlocked ? 1 : 0.7).drawRoundedRect((p.x - 120) * mx, (p.y - 40 - MAP_TOP) * my, 240 * mx, 110 * my, 2).endFill(); });
        for (const p of issues.messes) this.mini.beginFill(0xc9a227).drawRect(p.x * mx - 2.5, (p.y - MAP_TOP) * my - 2.5, 5, 5).endFill();
        for (const p of issues.impatient) this.mini.beginFill(0xf2994a).drawCircle(p.x * mx, (p.y - MAP_TOP) * my, 3.2).endFill();
        for (const p of issues.problems) this.mini.lineStyle(2, 0xffffff).beginFill(0xe5484d).drawCircle(p.x * mx, (p.y - MAP_TOP) * my, 6).endFill().lineStyle(0);
        for (const id of ids(g, AirportAgent)) {
            const p = g.getTypedUnsafe(Transform, id);
            if (id === pid) this.mini.lineStyle(1.5, C.navy).beginFill(C.gold).drawCircle(p.x * mx, (p.y - MAP_TOP) * my, 4).endFill().lineStyle(0);
            else this.mini.beginFill(C.white).drawCircle(p.x * mx, (p.y - MAP_TOP) * my, 2.6).endFill();
        }
    }
    /** Things on the floor that need a captain: crises (a fight, a breakdown...), impatient travellers and loose litter. */
    private issues(g: ReadOnlyGameModel) {
        const e = state(g), at = (id: number) => g.getTypedUnsafe(Transform, id);
        const problems = activeCrises(e).map(c => crisisPoint(c.crisis.station));
        const impatient = ids(g, AirportPassenger).filter(id => { const p = g.getTypedUnsafe(AirportPassenger, id); return !p.arriving && (p.patience <= ANGRY_PATIENCE || p.happiness < 40); }).map(at);
        const messes = ids(g, AirportTrash).filter(id => g.getTypedUnsafe(AirportTrash, id).carrier < 0).map(at);
        return { problems, impatient, messes };
    }
}

/** Row runs of minimap cells outside an outline (below the gate wall), cached per outline. */
const outsideCache = new WeakMap<readonly number[], { x: number; y: number; w: number; h: number }[]>();
function outsideRuns(outline: readonly number[]) {
    let runs = outsideCache.get(outline);
    if (runs) return runs;
    runs = [];
    const step = 40;
    for (let y = 340; y < CONFIG.height; y += step) {
        let start = -1;
        for (let x = 0; x <= CONFIG.width; x += step) {
            const out = x < CONFIG.width && !insidePolygon(outline, x + step / 2, y + step / 2);
            if (out && start < 0) start = x;
            if (!out && start >= 0) { runs.push({ x: start, y, w: x - start, h: step }); start = -1; }
        }
        if (start >= 0) runs.push({ x: start, y, w: CONFIG.width - start, h: step });
    }
    outsideCache.set(outline, runs);
    return runs;
}
